import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePppApi } from '../services/pppApi';

// ── helpers ──────────────────────────────────────────────────────────────────

function fmt(dateStr) {
  return dateStr ? new Date(dateStr).toLocaleDateString('it-IT') : '—';
}

function useApi(fetcher, deps = []) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetcher({
      onSuccess: (d) => { setData(d); setLoading(false); },
      onError:   ()  => setLoading(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading };
}

// ── KanbanFasiWidget ─────────────────────────────────────────────────────────

const STATO_FASE_STYLE = {
  non_avviata: { dot: 'bg-gray-300',  text: 'text-gray-400',  ring: 'ring-gray-200' },
  in_corso:    { dot: 'bg-blue-500',  text: 'text-blue-600',  ring: 'ring-blue-200' },
  conclusa:    { dot: 'bg-green-500', text: 'text-green-600', ring: 'ring-green-200' },
  sospesa:     { dot: 'bg-yellow-400',text: 'text-yellow-600',ring: 'ring-yellow-200' },
};

function FaseDot({ fase }) {
  const style = STATO_FASE_STYLE[fase.stato] ?? STATO_FASE_STYLE.non_avviata;
  const ritardo = fase.inRitardo;

  return (
    <div className="flex flex-col items-center gap-0.5" title={`F${fase.numeroFase} — ${fase.nome}`}>
      <div
        className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white ring-2 ${style.ring} ${ritardo ? 'bg-red-500 ring-red-200' : style.dot}`}
      >
        {fase.numeroFase}
      </div>
    </div>
  );
}

export function KanbanFasiWidget({ _locate }) {
  const api = usePppApi();
  const [procedimenti, setProcedimenti] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    api.getDashboardKanban({
      onSuccess: (d) => { setProcedimenti(d); setLoading(false); },
      onError:   ()  => setLoading(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibili = (procedimenti ?? []).filter(p =>
    !filtro ||
    p.titolo.toLowerCase().includes(filtro.toLowerCase()) ||
    (p.codice ?? '').toLowerCase().includes(filtro.toLowerCase()) ||
    (p.rupNome ?? '').toLowerCase().includes(filtro.toLowerCase())
  );

  return (
    <div className="bg-white dark:bg-secondary-dark-bg rounded-card shadow-card p-5 col-span-1 md:col-span-2 xl:col-span-3">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-0.5">PPP</p>
          <h2 className="text-base font-bold text-gray-800 dark:text-dark-text">
            {_locate('ppp_widget_kanban_titolo')}
          </h2>
        </div>
        <input
          type="text"
          placeholder={_locate('ppp_widget_kanban_cerca')}
          value={filtro}
          onChange={e => setFiltro(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-400 w-56"
        />
      </div>

      {/* Legenda */}
      <div className="flex flex-wrap gap-4 mb-4 text-xs text-gray-500">
        {[
          { color: 'bg-gray-300',  label: _locate('ppp_fase_non_avviata') },
          { color: 'bg-blue-500',  label: _locate('ppp_fase_in_corso') },
          { color: 'bg-green-500', label: _locate('ppp_fase_completata') },
          { color: 'bg-red-500',   label: _locate('ppp_fase_in_ritardo') },
        ].map(({ color, label }) => (
          <span key={label} className="flex items-center gap-1">
            <span className={`w-3 h-3 rounded-full ${color}`} />
            {label}
          </span>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 py-6 text-center">{_locate('ppp_caricamento')}</div>
      ) : visibili.length === 0 ? (
        <div className="text-sm text-gray-400 py-6 text-center">{_locate('ppp_widget_kanban_vuoto')}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-400 uppercase tracking-wide w-64">
                  {_locate('ppp_col_titolo')}
                </th>
                <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  {_locate('ppp_rup')}
                </th>
                <th className="text-left py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  {_locate('ppp_widget_kanban_fasi')} (F1–F14)
                </th>
              </tr>
            </thead>
            <tbody>
              {visibili.map(p => (
                <tr
                  key={p.id}
                  className="border-b border-gray-50 hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer"
                  onClick={() => navigate(`/ppp/${p.id}`)}
                >
                  <td className="py-3 pr-4">
                    <p className="font-medium text-gray-800 dark:text-dark-text truncate max-w-[16rem]">
                      {p.codice ? <span className="text-gray-400 mr-1">[{p.codice}]</span> : null}
                      {p.titolo}
                    </p>
                    <p className="text-xs text-gray-400">{p.enteConcedente}</p>
                  </td>
                  <td className="py-3 pr-4 text-gray-600 dark:text-gray-400 text-xs whitespace-nowrap">
                    {p.rupNome ?? '—'}
                  </td>
                  <td className="py-3">
                    <div className="flex items-center gap-1 flex-wrap">
                      {Array.from({ length: 14 }, (_, i) => {
                        const fase = p.fasi.find(f => f.numeroFase === i + 1);
                        if (fase) return <FaseDot key={i} fase={fase} />;
                        return (
                          <div key={i} className="w-5 h-5 rounded-full bg-gray-100 ring-2 ring-gray-100 flex items-center justify-center text-[9px] text-gray-300">
                            {i + 1}
                          </div>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── ScadenzeImminentiWidget ──────────────────────────────────────────────────

const STATO_VISIVO_STYLE = {
  scaduta:   'bg-red-100 text-red-700',
  oggi:      'bg-red-100 text-red-700',
  critica:   'bg-orange-100 text-orange-700',
  a_rischio: 'bg-yellow-100 text-yellow-700',
  imminente: 'bg-blue-100 text-blue-700',
};

export function ScadenzeImminentiWidget({ _locate }) {
  const api = usePppApi();
  const [giorni, setGiorni] = useState(7);
  const [scadenze, setScadenze] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.getScadenzeImminenti(giorni, {
      onSuccess: (d) => { setScadenze(d); setLoading(false); },
      onError:   ()  => setLoading(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giorni]);

  return (
    <div className="bg-white dark:bg-secondary-dark-bg rounded-card shadow-card p-5 col-span-1 md:col-span-2">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-0.5">PPP</p>
          <h2 className="text-base font-bold text-gray-800 dark:text-dark-text">
            {_locate('ppp_widget_scadenze_lista_titolo')}
          </h2>
        </div>
        <div className="flex gap-1">
          {[7, 15, 30].map(g => (
            <button
              key={g}
              onClick={() => setGiorni(g)}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                giorni === g
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {g}gg
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 py-4 text-center">{_locate('ppp_caricamento')}</div>
      ) : !scadenze?.length ? (
        <div className="text-sm text-gray-400 py-4 text-center">{_locate('ppp_widget_scadenze_lista_vuota')}</div>
      ) : (
        <ul className="divide-y divide-gray-50 max-h-72 overflow-y-auto">
          {scadenze.map(s => (
            <li
              key={s.scadenzaId}
              className="py-2.5 flex items-start justify-between gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 -mx-1 px-1 rounded"
              onClick={() => navigate(`/ppp/${s.procedimentoId}`, { state: { tab: 1 } })}
            >
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-700 dark:text-dark-text truncate">
                  {s.codiceProcedimento ? <span className="text-gray-400">[{s.codiceProcedimento}] </span> : null}
                  {s.titoloProcedimento}
                </p>
                <p className="text-xs text-gray-500 truncate">{s.descrizione}</p>
                <p className="text-xs text-gray-400 mt-0.5">{fmt(s.dataScadenza)}</p>
              </div>
              <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${STATO_VISIVO_STYLE[s.statoVisivo] ?? 'bg-gray-100 text-gray-500'}`}>
                {s.giorniRimanenti < 0
                  ? `+${Math.abs(s.giorniRimanenti)}gg`
                  : s.giorniRimanenti === 0
                    ? _locate('ppp_widget_oggi')
                    : `${s.giorniRimanenti}gg`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── KpiFasiWidget ────────────────────────────────────────────────────────────

export function KpiFasiWidget({ _locate }) {
  const api = usePppApi();
  const { data: kpi, loading } = useApi(cb => api.getKpiFasi(cb));

  if (loading) {
    return (
      <div className="bg-white dark:bg-secondary-dark-bg rounded-card shadow-card p-5 col-span-1">
        <p className="text-sm text-gray-400">{_locate('ppp_caricamento')}</p>
      </div>
    );
  }

  const fasi = (kpi ?? []).filter(f => f.totaleConcluse > 0 || f.termineLegaleGiorni);

  return (
    <div className="bg-white dark:bg-secondary-dark-bg rounded-card shadow-card p-5 col-span-1 md:col-span-2 xl:col-span-3">
      <div className="mb-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-0.5">PPP — KPI</p>
        <h2 className="text-base font-bold text-gray-800 dark:text-dark-text">
          {_locate('ppp_widget_kpi_titolo')}
        </h2>
        <p className="text-xs text-gray-400 mt-0.5">{_locate('ppp_widget_kpi_sottotitolo')}</p>
      </div>

      {!fasi.length ? (
        <p className="text-sm text-gray-400 py-4 text-center">{_locate('ppp_widget_kpi_vuoto')}</p>
      ) : (
        <div className="space-y-3">
          {fasi.map(f => {
            const legale  = f.termineLegaleGiorni ?? 0;
            const medio   = f.tempoMedioGiorni ?? 0;
            const maxBar  = Math.max(legale, medio, 1);
            const pctLeg  = Math.round((legale / maxBar) * 100);
            const pctMed  = Math.round((medio / maxBar) * 100);
            const inRitardo = medio > 0 && legale > 0 && medio > legale;

            return (
              <div key={f.numeroFase}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700 dark:text-dark-text">
                    <span className="text-gray-400 mr-1">F{f.numeroFase}</span>
                    {f.nome}
                  </span>
                  <span className="text-xs text-gray-400">
                    {f.totaleConcluse > 0
                      ? `${_locate('ppp_widget_kpi_media')}: ${Math.round(medio)}gg`
                      : _locate('ppp_widget_kpi_nessun_dato')}
                    {f.termineLegaleGiorni ? ` / ${f.termineLegaleGiorni}gg ${_locate('ppp_widget_kpi_legale')}` : ''}
                  </span>
                </div>
                <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                  {legale > 0 && (
                    <div
                      className="absolute top-0 left-0 h-full bg-gray-300 rounded-full"
                      style={{ width: `${pctLeg}%` }}
                    />
                  )}
                  {medio > 0 && (
                    <div
                      className={`absolute top-0 left-0 h-full rounded-full opacity-80 ${inRitardo ? 'bg-red-400' : 'bg-teal-500'}`}
                      style={{ width: `${pctMed}%` }}
                    />
                  )}
                </div>
              </div>
            );
          })}

          <div className="flex gap-4 mt-3 text-xs text-gray-400">
            <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-gray-300 inline-block" />{_locate('ppp_widget_kpi_termine_legale')}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-teal-500 inline-block" />{_locate('ppp_widget_kpi_tempo_medio_ok')}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-red-400 inline-block" />{_locate('ppp_widget_kpi_tempo_medio_ritardo')}</span>
          </div>
        </div>
      )}
    </div>
  );
}
